import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, finalize, of, shareReplay } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { assert } from '../../../core/assert/assert';
import { FileModel } from '../../../core/models/file/file.model';
import { AnnualVacationQuotaModel } from '../../../core/models/user/annual-vacation-quota.model';
import { UserModel } from '../../../core/models/user/user.model';
import { WorkingTimeModel } from '../../../core/models/user/working-time.model';
import { WorkingTimeService } from '../../../core/services/features/working-time/working-time.service';
import { selectFile } from '../../../core/state/files/files.selectors';
import { UsersHttpService } from '../../../core/state/users/users-http.service';
import { selectUser } from '../../../core/state/users/users.selectors';
import { FixInterval } from '../../../features/calendar/components/time-block/types/time-block-types';

@Injectable()
export class UserService {
  private readonly workingTimeService = inject(WorkingTimeService);
  private readonly store = inject(Store);
  private readonly usersHttpService = inject(UsersHttpService);
  private readonly inFlight = new Map<number, Observable<UserModel>>();

  // Function that builds an unassigned user model if null is passed. A default user model has an id of -1.
  // Note: If a default user is sent to the server, it needs to be null.
  public static buildDefaultUser(): UserModel {
    const defaultUser = new UserModel();
    // Set -1 as dummy id since null leads to problems with the dropdowns.
    defaultUser.id = -1;
    return defaultUser;
  }

  public filterAlreadyAddedMembers(
    allMembers: readonly UserModel[],
    projectMembers: readonly UserModel[],
  ): UserModel[] {
    return allMembers.filter(
      (member) => projectMembers.findIndex((element) => element.id === member.id) < 0,
    );
  }

  public calculateWorkingSeconds(user: UserModel, fixIntervalType: FixInterval): number {
    const year = user.employeeHireDate!.getFullYear();
    const endDate = new Date(year, 11, 31); // Note: months are 0-based in JS; 11 = December

    let totalSeconds = 0;

    const currentDate = new Date(user.employeeHireDate!);
    while (currentDate <= endDate) {
      totalSeconds += this.workingTimeService.getWorkingTime(currentDate, user, fixIntervalType);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return totalSeconds;
  }

  public getCurrentAnnualVacationQuotaEntry(user: UserModel): AnnualVacationQuotaModel {
    assert(user.annualVacationQuotaEntries.length > 0);
    return user.annualVacationQuotaEntries[user.annualVacationQuotaEntries.length - 1];
  }

  public overrideWorkingTimeEntries(
    workingTimeEntries: WorkingTimeModel[],
    user: UserModel,
  ): UserModel {
    user.workingTimeEntries = workingTimeEntries;
    return user;
  }

  public overrideAnnualVacationQuotaEntries(
    annualVacationQuotaEntries: AnnualVacationQuotaModel[],
    user: UserModel,
  ) {
    user.annualVacationQuotaEntries = annualVacationQuotaEntries;
    return user;
  }

  public getUserEnsured(userId: number): Observable<UserModel> {
    // 1) If already cached, return it immediately
    return this.store.select(selectUser(userId)).pipe(
      take(1),
      switchMap((cached) => {
        if (cached) {
          return of(cached);
        }

        // 2) If already loading, reuse the same observable
        const existing = this.inFlight.get(userId);
        if (existing) {
          return existing;
        }

        // 3) Start load (NgRx will do HTTP and reducer will cache on setSingleUser)
        this.usersHttpService.getUser(userId);

        const request$ = this.store.select(selectUser(userId)).pipe(
          filter((u): u is UserModel => !!u),
          take(1),
          shareReplay({ bufferSize: 1, refCount: true }),
          finalize(() => this.inFlight.delete(userId)),
        );

        this.inFlight.set(userId, request$);
        return request$;
      }),
    );
  }

  public getUserAvatarThumb$(userId): Observable<FileModel> {
    return this.store.select(selectFile(userId));
  }

  public getUserInitials(user: UserModel): string {
    const firstNameInitial = Array.from(user.firstName)[0];
    const lastNameInitial = Array.from(user.lastName)[0];
    return firstNameInitial + lastNameInitial;
  }
}
